import { Box } from '@mui/material';
import { getSlotEndMinutes, getSlotStartMinutes, minutesToTimelinePercent } from '../../helpers/time';
import CurrentTimeMarker from './components/CurrentTimeMarker';
import FocusTimeDaySlot from './components/FocusTimeDaySlot';
import TimelineGrid from './components/TimelineGrid';
import { MIN_DAY_HEIGHT } from './layout';
import { FocusTimeDayTimelineSlot } from './types';
import useFocusTimeDayTimelineActions from './useFocusTimeDayTimelineActions';

export type { FocusTimeDayTimelineSlot };

type FocusTimeDayTimelineProps = Readonly<{
  currentMinutes: number | null;
  onChange: (slots: FocusTimeDayTimelineSlot[]) => void;
  slots: FocusTimeDayTimelineSlot[];
}>;

const ROOT_SX = {
  width: '100%',
  height: '100%',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
} as const;

const TIMELINE_SX = {
  width: '100%',
  flex: '1 1 auto',
  minHeight: 0,
  pt: 1.25,
  pb: 1.25,
  overflowY: 'auto',
} as const;

const DAY_SX = {
  position: 'relative',
  height: '100%',
  minHeight: MIN_DAY_HEIGHT,
} as const;

const FocusTimeDayTimeline = ({ currentMinutes, onChange, slots }: FocusTimeDayTimelineProps) => {
  const {
    dayRef,
    draggingSlotId,
    timelineSlots,
    minMovableStartMinutes,
    addSlotFromTimeline,
    startResize,
    moveResize,
    endResize,
    startSlotMove,
    moveSlotByPointer,
    endSlotMove,
    removeSlot,
  } = useFocusTimeDayTimelineActions({ currentMinutes, onChange, slots });

  return (
    <Box sx={ROOT_SX}>
      <Box sx={TIMELINE_SX}>
        <Box ref={dayRef} sx={DAY_SX} onClick={addSlotFromTimeline}>
          <TimelineGrid />
          <CurrentTimeMarker currentMinutes={currentMinutes} />

          {timelineSlots.map(slot => {
            const startMinutes = getSlotStartMinutes(slot);
            const endMinutes = getSlotEndMinutes(slot);
            const top = minutesToTimelinePercent(startMinutes);
            const height = minutesToTimelinePercent(endMinutes) - top;

            return (
              <FocusTimeDaySlot
                key={slot.id}
                slot={slot}
                startMinutes={startMinutes}
                endMinutes={endMinutes}
                top={top}
                height={height}
                isDragging={draggingSlotId === slot.id}
                isStartDisabled={startMinutes < minMovableStartMinutes}
                onStartMove={startSlotMove}
                onMove={moveSlotByPointer}
                onEndMove={endSlotMove}
                onRemoveSlot={removeSlot}
                onStartResize={startResize}
                onMoveResize={moveResize}
                onEndResize={endResize}
              />
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default FocusTimeDayTimeline;
